import {useId, type ButtonHTMLAttributes, type ReactNode} from 'react';
import {Link} from '../app/router';
import {cx} from './cx';

/**
 * The five button weights this surface has, and no sixth.
 *
 * * `primary` — the **one** solid Chili Red action per screen. The red rule
 *   (foundations §5) is one forward action, and it is the main one: add the
 *   item, view the cart, send the order, pay, retry the payment. A screen with
 *   two solid reds has told the guest nothing.
 * * `secondary` — an ink outline. The alternative to the primary action.
 * * `tertiary` — a warm hairline with River Stone text. A way *out* rather than
 *   a way forward: "back to the bill", "check again later".
 * * `text` — an aside. Underlined, because without a border or a fill an
 *   underline is the only thing left that says this is pressable.
 * * `destructive` — outlined Dried Chili, **never filled**, and the label must
 *   contain a word rather than a bare ✕. This is the red rule's other half and
 *   `pokpokTheme.components.button['variant:destructive']` already encodes it.
 *
 * # Disabled says why
 *
 * The design draws a disabled primary (the item screen, before a required option
 * is answered) with no explanation next to it — the only clue is a small pill
 * further up the page. A guest who taps and gets nothing has been told nothing.
 * So `disabledReason` is a required companion to `disabled` on this component:
 * pass the localised sentence, and it becomes the button's accessible
 * description and a visible line beneath it.
 *
 * A disabled button is still **rendered and still focusable**, using
 * `aria-disabled` rather than the `disabled` attribute, because a `disabled`
 * button is removed from the tab order and cannot announce its own reason.
 * `onClick` is suppressed here, so a caller cannot forget.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'text' | 'destructive';

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'disabled' | 'children' | 'type'
>;

export interface ButtonProps extends NativeButtonProps {
  variant?: ButtonVariant;
  /** Full width. The default for `primary`, because that is how it is drawn. */
  block?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /**
   * Localised, and required whenever `disabled` is true. Announced as the
   * button's description, and shown under it unless `reasonVisible` is false.
   */
  disabledReason?: string;
  /**
   * Whether the reason is *drawn* as well as announced. Default true.
   *
   * `false` is for the case where the screen already says it somewhere a sighted
   * guest is looking — Q4's required-option pill, for instance, which sits on
   * the group the button is waiting for. The description stays either way: a
   * screen reader has no pill to glance at, and dropping the sentence for
   * everybody to tidy the layout would take the affordance from the one guest
   * who cannot recover it.
   */
  reasonVisible?: boolean;
  /** `button` unless the control genuinely submits a form. */
  type?: 'button' | 'submit';
}

export function Button({
  variant = 'secondary',
  block,
  children,
  className,
  disabled = false,
  disabledReason,
  reasonVisible = true,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  const full = block ?? variant === 'primary';
  // `useId` unconditionally, and used conditionally: an id that changed between
  // renders would repoint `aria-describedby` at a node that no longer exists,
  // and a screen reader mid-sentence would simply stop.
  const generatedId = useId();
  const reasonId = disabled && disabledReason ? generatedId : undefined;

  const button = (
    <button
      {...rest}
      type={type}
      aria-disabled={disabled || undefined}
      aria-describedby={reasonId}
      onClick={disabled ? undefined : onClick}
      className={cx(
        'pk-btn',
        `pk-btn--${variant}`,
        full && 'pk-btn--block',
        variant === 'primary' && 'pokpok-primary-action',
        className,
      )}
    >
      {children}
    </button>
  );

  if (!reasonId) return button;

  return (
    <>
      {button}
      <p id={reasonId} className={reasonVisible ? 'pk-hint' : 'pokpok-sr-only'}>
        {disabledReason}
      </p>
    </>
  );
}

/**
 * The same five weights, as a link.
 *
 * Several of the design's primary actions are navigations — view cart, see
 * status, back to the menu — and a navigation must be an `<a href>`: it is
 * long-pressable, it is announced as a link, and it works when the click handler
 * has not hydrated yet. `Link` from the router renders exactly that and only
 * intercepts a plain left click.
 *
 * There is no disabled state here on purpose. A link that goes nowhere is not a
 * link; render a `Button` with `disabled` and a reason, or render nothing.
 */
export function ActionLink({
  to,
  variant = 'secondary',
  block,
  replace,
  children,
  className,
  ...rest
}: {
  to: string;
  variant?: ButtonVariant;
  block?: boolean;
  replace?: boolean;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}) {
  const full = block ?? variant === 'primary';
  return (
    <Link
      {...rest}
      to={to}
      replace={replace}
      className={cx(
        'pk-btn',
        `pk-btn--${variant}`,
        full && 'pk-btn--block',
        variant === 'primary' && 'pokpok-primary-action',
        'pokpok-tap',
        className,
      )}
    >
      {children}
    </Link>
  );
}
